/**
 * Pure helpers behind the `UnixTimeTool` page. Tests live in
 * `tests/web/unixTimeTool.test.ts`.
 *
 * Scope (v1):
 *   - Decode `<unix timestamp>` ↔ structured fields (UTC / local / ISO 8601 / relative).
 *   - Encode `<datetime-local string>` (in 'local' or 'utc' tz) → seconds + milliseconds.
 *
 * Out of scope (will need a TZ-aware library to add):
 *   - Arbitrary IANA timezones.
 *   - DST math beyond what the runtime's Intl already gives us.
 */

export type UnixUnit = 'seconds' | 'milliseconds' | 'auto';

export interface UnixDecoded {
  /** Resolved unit (no 'auto'). */
  unit: 'seconds' | 'milliseconds';
  /** `'YYYY-MM-DD HH:mm:ss UTC'`. */
  utc: string;
  /** `'YYYY-MM-DD HH:mm:ss (Asia/Shanghai)'` — uses `Intl.DateTimeFormat`'s zone. */
  local: string;
  /** ISO 8601, always UTC ('Z' suffix). */
  iso: string;
  /** Relative phrase like '5 分钟前' / '昨天' / '刚刚'. */
  relative: string;
}

const SECOND_MS = 1000;

export function decodeUnixTime(
  input: string,
  unit: UnixUnit,
  now: number = Date.now(),
): { ok: true; data: UnixDecoded } | { ok: false; error: string } {
  const trimmed = input.trim();
  if (trimmed.length === 0) return { ok: false, error: '输入为空' };

  // Allow a leading minus for pre-epoch values; everything else must
  // be base-10 digits. Reject scientific notation ("1e9") because the
  // tool is meant to mirror what users copy out of logs / DB columns.
  if (!/^-?\d+$/.test(trimmed)) {
    return { ok: false, error: '时间戳必须是十进制整数' };
  }

  const resolvedUnit = resolveUnit(trimmed, unit);
  if (!resolvedUnit) {
    return {
      ok: false,
      error: '无法自动判断时间戳单位（应为 10 位秒或 13 位毫秒）',
    };
  }

  const value = Number(trimmed);
  if (!Number.isFinite(value)) {
    return { ok: false, error: '时间戳超过 JavaScript 数值精度' };
  }

  const ms = resolvedUnit === 'seconds' ? value * SECOND_MS : value;
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) {
    return { ok: false, error: '时间戳超出 JavaScript 可表达的范围' };
  }

  return {
    ok: true,
    data: {
      unit: resolvedUnit,
      utc: formatUtc(date),
      local: formatLocal(date),
      iso: date.toISOString(),
      relative: formatRelative(ms - now),
    },
  };
}

export function encodeUnixTime(
  human: string,
  tz: 'local' | 'utc',
): { ok: true; seconds: number; milliseconds: number } | { ok: false; error: string } {
  const trimmed = human.trim();
  if (trimmed.length === 0) return { ok: false, error: '输入为空' };

  // `<input type="datetime-local">` emits "2026-06-01T21:15" or
  // "2026-06-01T21:15:00". The `Date(string)` constructor parses
  // those as local time when there is no zone designator, which is
  // exactly what we want for the 'local' branch. For 'utc' we
  // append 'Z' so the same parser interprets it as UTC.
  const normalised = tz === 'utc' ? `${stripZoneSuffix(trimmed)}Z` : trimmed;
  const date = new Date(normalised);
  if (Number.isNaN(date.getTime())) {
    return { ok: false, error: `无法解析为日期时间：${trimmed}` };
  }
  const milliseconds = date.getTime();
  return {
    ok: true,
    milliseconds,
    seconds: Math.floor(milliseconds / SECOND_MS),
  };
}

function resolveUnit(numeric: string, unit: UnixUnit): 'seconds' | 'milliseconds' | undefined {
  if (unit === 'seconds' || unit === 'milliseconds') return unit;
  // Strip the leading minus so '-86400' (1969-12-31) is treated like
  // a 5-digit positive value for length-based detection.
  const digits = numeric.startsWith('-') ? numeric.slice(1) : numeric;
  if (digits.length === 13) return 'milliseconds';
  if (digits.length === 9 || digits.length === 10) return 'seconds';
  return undefined;
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

function formatUtc(d: Date): string {
  return (
    `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())} ` +
    `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())} UTC`
  );
}

function formatLocal(d: Date): string {
  const tz =
    typeof Intl !== 'undefined'
      ? new Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'UTC';
  return (
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
    `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())} ` +
    `(${tz})`
  );
}

/**
 * Self-contained relative time formatter. Doesn't pull in
 * `Intl.RelativeTimeFormat` because the strings we want are slightly
 * different from the canonical translations (e.g. we say "刚刚" inside
 * a 5 s window, not "0 秒前").
 *
 * Argument is `ms = then - now` so positive = future, negative = past.
 */
function formatRelative(ms: number): string {
  const absMs = Math.abs(ms);
  const isFuture = ms > 0;
  const suffix = isFuture ? '后' : '前';

  if (absMs < 5_000) return '刚刚';
  if (absMs < 60_000) return `${Math.floor(absMs / 1000)} 秒${suffix}`;
  if (absMs < 60 * 60_000) return `${Math.floor(absMs / 60_000)} 分钟${suffix}`;
  if (absMs < 24 * 60 * 60_000) return `${Math.floor(absMs / (60 * 60_000))} 小时${suffix}`;
  if (absMs < 2 * 24 * 60 * 60_000) return isFuture ? '明天' : '昨天';
  if (absMs < 30 * 24 * 60 * 60_000) {
    return `${Math.floor(absMs / (24 * 60 * 60_000))} 天${suffix}`;
  }
  if (absMs < 365 * 24 * 60 * 60_000) {
    return `${Math.floor(absMs / (30 * 24 * 60 * 60_000))} 个月${suffix}`;
  }
  return `${Math.floor(absMs / (365 * 24 * 60 * 60_000))} 年${suffix}`;
}

function stripZoneSuffix(s: string): string {
  // Strip an existing 'Z' or '+HH:MM' / '-HH:MM' so we can re-attach
  // 'Z' cleanly. Doesn't touch the rest of the string.
  return s.replace(/(?:Z|[+-]\d{2}:\d{2})$/, '');
}
